import React, { useMemo } from "react";
import DottedMap from "dotted-map";
import { motion } from "framer-motion";

/* Aceternity world-map, adapted for BookingFlows — light dotted world with
   animated brand-blue connection arcs. */
export const WorldMap = ({ dots = [], lineColor = "#2563EB" }) => {
  const svgMap = useMemo(() => {
    const map = new DottedMap({ height: 100, grid: "diagonal" });
    return map.getSVG({
      radius: 0.22,
      color: "#CBD5E1",
      shape: "circle",
      backgroundColor: "white",
    });
  }, []);

  const projectPoint = (lat, lng) => {
    const x = (lng + 180) * (800 / 360);
    const y = (90 - lat) * (400 / 180);
    return { x, y };
  };

  const createCurvedPath = (start, end) => {
    const midX = (start.x + end.x) / 2;
    const midY = Math.min(start.y, end.y) - 50;
    return `M ${start.x} ${start.y} Q ${midX} ${midY} ${end.x} ${end.y}`;
  };

  return (
    <div className="relative w-full aspect-[2/1] rounded-2xl bg-white font-sans">
      <img
        src={`data:image/svg+xml;utf8,${encodeURIComponent(svgMap)}`}
        className="pointer-events-none h-full w-full select-none [mask-image:linear-gradient(to_bottom,transparent,white_10%,white_90%,transparent)]"
        alt="world map"
        height="495"
        width="1056"
        draggable={false}
      />
      <svg viewBox="0 0 800 400" className="pointer-events-none absolute inset-0 h-full w-full select-none">
        <defs>
          <linearGradient id="bf-map-path-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="white" stopOpacity="0" />
            <stop offset="5%" stopColor={lineColor} stopOpacity="1" />
            <stop offset="95%" stopColor={lineColor} stopOpacity="1" />
            <stop offset="100%" stopColor="white" stopOpacity="0" />
          </linearGradient>
        </defs>
        {dots.map((dot, i) => {
          const startPoint = projectPoint(dot.start.lat, dot.start.lng);
          const endPoint = projectPoint(dot.end.lat, dot.end.lng);
          return (
            <g key={`path-group-${i}`}>
              <motion.path
                d={createCurvedPath(startPoint, endPoint)}
                fill="none"
                stroke="url(#bf-map-path-gradient)"
                strokeWidth="1.2"
                initial={{ pathLength: 0 }}
                whileInView={{ pathLength: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 1.4, delay: 0.4 * i, ease: "easeOut" }}
              />
              {[dot.start, dot.end].map((point, pi) => {
                const p = projectPoint(point.lat, point.lng);
                return (
                  <g key={`points-${i}-${pi}`}>
                    <circle cx={p.x} cy={p.y} r="2.5" fill={lineColor} />
                    <circle cx={p.x} cy={p.y} r="2.5" fill={lineColor} opacity="0.5">
                      <animate attributeName="r" from="2.5" to="9" dur="1.6s" begin="0s" repeatCount="indefinite" />
                      <animate attributeName="opacity" from="0.5" to="0" dur="1.6s" begin="0s" repeatCount="indefinite" />
                    </circle>
                  </g>
                );
              })}
            </g>
          );
        })}
      </svg>
    </div>
  );
};

export default WorldMap;
